import { inject, Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router'
import { EMPTY, Observable, of } from 'rxjs'
import { catchError, filter, switchMap } from 'rxjs/operators'
import { MsalBroadcastService, MsalService, } from '@azure/msal-angular'
import { RedirectRequest } from '@azure/msal-browser'
import { InteractionStatus } from '@azure/msal-browser'
import {
   AppSignals,
   AuthService
} from '@appShared/services'

import { AppFacade } from '@appShared/services/app.facade'
import { IProfile } from '@appShared/interfaces/[Model-based]/profile.interface'
import { SITE_DIRECTORY_ROUTES } from '@appShared/site-directory.routes'
import { defaultSignInAuthority } from '@appShared/b2c-policies'
import { setErrorMessage } from '@appShared/utils/error-message'
import { environment } from '@appEnvironments/environment'
import { IUser } from '../interfaces/[Interface-based]'

export interface MsalUserGuardRequest {
   route?: ActivatedRouteSnapshot
   state?: RouterStateSnapshot
   returnUrl?: string
   allowPromo?: boolean
   customerServiceRequired?: boolean
}

@Injectable({ providedIn: 'root' })
export class MsalUserGuardService {
   private _authService = inject(AuthService)
   private _appFacade = inject(AppFacade)
   private _msalService = inject(MsalService)
   private _msalBroadcastService = inject(MsalBroadcastService)
   private _appSignals = inject(AppSignals)
   private _router = inject(Router)
   private _contentUrl = `/${SITE_DIRECTORY_ROUTES.customerService.uri}`
   //private _accountIssueUrl = `/${SITE_DIRECTORY_ROUTES.accountIssue.baseUri}`
   private _errorPageUrl = `/${SITE_DIRECTORY_ROUTES.error.uri}`
   private _unauthorizedUrl = `/${SITE_DIRECTORY_ROUTES.unauthorized.uri}`
   //KEEP - might use this in future if need to supportmobile App
   //private _hybridService = inject(HybridWebviewInteropService)
   //private _isDotNetInteroperable = toSignal(this._hybridService.isDotNetInteroperable$)
   private _isDevTest = !environment.production

   /*
    public methods
   */

   msalNonAuthenticated(returnUrl?: string): Observable<boolean> {
      return this._msalBroadcastService.inProgress$
         .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            switchMap(() => {

               if (this._msalService.instance.getAllAccounts().length > 0) {

                  if (this._isDevTest) {
                     console.log('msalNonAuthenticated:LOGGED IN - getting current user', (new Date()).getTime())
                  }

                  return this._appFacade.refreshCurrentUser(/*returnCacheIfExists*/true)
                     .pipe(
                        switchMap((user) => {

                           // Authenticated but no user/profile resolved (204 from /user)
                           // - an access problem, not a fault
                           if (!user) {
                              this._router.navigateByUrl(this._unauthorizedUrl)
                              return of(false)
                           }

                           this._router.navigateByUrl(returnUrl || this._contentUrl)
                           return EMPTY

                        })
                     )
               }

               if (this._isDevTest) {
                  console.log('msalNonAuthenticated:NOT LOGGED IN', (new Date()).getTime())
               }

               return of(true)
            }),
            catchError(this._catchError.bind(this))
         )
   }

   msalAuthenticated(returnUrl?: string): Observable<boolean> {

      return this._msalBroadcastService.inProgress$
         .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            switchMap(() => {

               if (this._msalService.instance.getAllAccounts().length === 0) {

                  if (this._isDevTest) {
                     console.log('msalAuthenticated:NOT LOGGED IN', (new Date()).getTime())
                  }

                  if (returnUrl?.includes('registration?token')) {
                     this._authService.login({ authority: defaultSignInAuthority.authority, state: returnUrl } as RedirectRequest)
                     return EMPTY
                  }

                  returnUrl = returnUrl || this._contentUrl
                  this._authService.redirectUrl.set(returnUrl)

                  this._router.navigateByUrl('/')

                  return EMPTY
               }

               if (this._isDevTest) {
                  console.log('msalAuthenticated:LOGGED IN', (new Date()).getTime())
               }

               return of(true)
            }),
            catchError(this._catchError.bind(this))
         )
   }

   msalAuthGuard(msalUserGuardRequest?: MsalUserGuardRequest): Observable<boolean> {
      const guardRequest = this._configureGuardRequest(msalUserGuardRequest)

      if (this._isDevTest) {
         console.log('msalAuthGuard:requesting route ', msalUserGuardRequest.returnUrl, (new Date()).getTime())
      }

      //TODO: REMOVE
      //return of(true)

      return this._msalUser(guardRequest).pipe(
         switchMap((user) => this._verifyUserStatus(guardRequest, user)),
         catchError(this._catchError.bind(this))
      )
   }

   /*
    private methods
   */

   private _catchError(err): Observable<never> {
      // err may be a preserved HttpErrorResponse - store display text, not the object
      this._appSignals.setError(typeof err === 'string' ? err : setErrorMessage(err))
      this._router.navigateByUrl(this._errorPageUrl)
      return EMPTY
   }

   private _configureGuardRequest(msalUserGuardRequest?: MsalUserGuardRequest): MsalUserGuardRequest {
      const guardRequest = {
         ...msalUserGuardRequest,
         returnUrl: msalUserGuardRequest?.returnUrl
            || msalUserGuardRequest?.state.url
            || '/'
      } as MsalUserGuardRequest

      return guardRequest
   }

   private _msalUser(msalUserGuardRequest?: MsalUserGuardRequest, user?: IUser): Observable<IUser> {

      if (this._isDevTest) {
         console.log('in _msalUser', (new Date()).getTime())
      }

      return this.msalAuthenticated(msalUserGuardRequest?.returnUrl).pipe(
         filter((authenticated: boolean) => authenticated),
         switchMap(() => this._appFacade.refreshCurrentUser(/*returnCacheIfExists*/true))
      )
   }

   private _verifyUserStatus(
      msalUserGuardRequest: MsalUserGuardRequest,
      user: IUser): Observable<boolean> {

      const returnUrl = msalUserGuardRequest.returnUrl

      // Authenticated but no user/profile resolved (204 from /user)
      // - an access problem, not a fault
      if (!user) {
         if (this._isDevTest) {
            console.log('_verifyUserStatus: no user/profile resolved - unauthorized', returnUrl, (new Date()).getTime())
         }

         this._router.navigateByUrl(this._unauthorizedUrl)
         return of(false)
      }

      // Customer-service routes require the customer-service role.
      // Send role-less users to the guard-free unauthorized page — sending them
      // to '/' bounces logged-in users right back here (infinite redirect loop).
      if (msalUserGuardRequest.customerServiceRequired && !user.isCustomerService) {
         if (this._isDevTest) {
            console.log('_verifyUserStatus: user is not in the customer-service role - unauthorized', returnUrl, (new Date()).getTime())
         }

         this._router.navigateByUrl(this._unauthorizedUrl)
         return of(false)
      }

      return of(true)
   }
}
